import * as React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Alert, { AlertCategoryEnum } from "../../../misc/components/Alert";
import differenceBy from "lodash-es/differenceBy";
import { size, groupBy, filter } from "lodash";
import { FixedCommentSetForm } from "../FixedCommentsForm/FixedCommentsForm";
import { FixedCommentSetFormModel, GetModelFromFormState, GetFormStateFromModel, GetSetFormStateFromModel } from "../Types";
import { AuditModal, AuditForm } from "../../../auditmodal/components/AuditModal";
import { StudyFixedCommentSet, WebPortalClient } from "webportalclient";
import { useCurrentStudyConfigurationContext } from "../../../Core/CurrentStudyConfigurationContext";
import { useWebPortalApiClient } from "../../../misc/api/WebPortalTokenClient";
import { FormState } from "informedv4";

const EditFixedCommentSetScreen = ({ }): JSX.Element => {

    const { id } = useParams<{ id: string }>();
    const [isBusy, setIsBusy] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string>();
    const [initialState, setInitialState] = React.useState<FixedCommentSetFormModel>();
    const [showAuditModal, setShowAuditModal] = React.useState<boolean>(false);
    const [formToSubmit, setFormToSubmit] = React.useState<StudyFixedCommentSet>();
    const [successAlert, setSuccessAlert] = React.useState(false);
    const client = useWebPortalApiClient();

    const navigate = useNavigate();
    const { study } = useCurrentStudyConfigurationContext();


    const onSubmit = async (formState: FormState) => {
        const values: FixedCommentSetFormModel = formState.values as unknown as FixedCommentSetFormModel;

        const modelToSubmit = GetModelFromFormState({
            ...values,
            Id: id
        }, study.id);

        try {
            const fixedComments = await client.client.getFixedComments(undefined, `setId eq ${modelToSubmit.id}`);
            const currentComments = modelToSubmit.fixedCommentSets[0]?.comments ?? [];
            const toDelete = differenceBy(fixedComments.value, currentComments, c => c.id);

            const remainingCount = fixedComments["@odata.count"] - toDelete.length + currentComments.length;

            if (remainingCount < 1) {
                setSuccessAlert(false);
                setError("There must be at least one fixed comment");
                return;
            }

            const duplicateComments = filter(groupBy(values.Comments, c => c.comment), g => g.length > 1);

            if (duplicateComments.length > 0) {
                setSuccessAlert(false);
                setError("Two comments cannot have the same display text");
                return;
            }

            const duplicateDescriptions = filter(groupBy(filter(values.Comments, c => c.description !== "" && c.description !== undefined && c.description !== null), c => c.description), g => g.length > 1);

            if (duplicateDescriptions.length > 0) {
                setSuccessAlert(false);
                setError("Two comment descriptions cannot have the same text");
                return;
            }

            setFormToSubmit(modelToSubmit);
            setError(null)
            setShowAuditModal(true);
        } catch (e) {
            setSuccessAlert(false);
            setError(e.message);
        }
    };


    React.useEffect(() => {

        const getFormState = async () => {
            setIsBusy(true);

            try {
                const set = await client.client.getFixedCommentSet(id, undefined, "comments,studySet");
                setInitialState(GetSetFormStateFromModel(set));
            } catch (e) {
                setError(e.message);
            } finally {
                setIsBusy(false);
            }
        };

        getFormState();
    }, [id]);


    const handleSubmit = async (values: AuditForm) => {
        setIsBusy(true);
        setSuccessAlert(false);
        setError(null);

        try {
            const fixedComments = await client.client.getFixedComments(undefined, `setId eq ${formToSubmit.id}`);
            const toDelete = differenceBy(fixedComments.value, formToSubmit.fixedCommentSets[0].comments, c => c.id);

            for (const deleteComment of toDelete) {
                await client.client.deleteFixedComment(deleteComment.id);
            }

            for (const comment of formToSubmit.fixedCommentSets[0].comments) {
                if (fixedComments.value.map(fc => fc.id).includes(comment.id)) {
                    await client.client.patchFixedComment({
                        ...comment,
                        description: comment.description ?? null,
                    }, comment.id, values.Annotation);
                } else {
                    await client.client.postFixedComment({ ...comment, setId: formToSubmit.id });
                }
            }

            await client.client.patchFixedCommentSet({ entityType: formToSubmit.fixedCommentSets[0].entityType }, formToSubmit.id, values.Annotation);

            setShowAuditModal(false);
            setIsBusy(false);
            setSuccessAlert(true);
        } catch (e) {
            setError(e.message);
            setShowAuditModal(false);
            setIsBusy(false);
        }
    };


    return (!!initialState ? <>
        <Alert show={successAlert} category={AlertCategoryEnum.Success}>
            Saved successfully. Return to{" "}
            <Link to={`/FixedComments`}>Fixed Comments list</Link>?
        </Alert>
        <FixedCommentSetForm editMode={true} title="Edit Fixed Comment Set" initialCommentsFormValues={initialState} onSubmit={onSubmit} error={error} />
        {showAuditModal ? <AuditModal onSubmit={handleSubmit} onClose={() => setShowAuditModal(false)} isSubmitting={isBusy} header="Edit Fixed Comments" /> : null}
    </> : null);
};

export default EditFixedCommentSetScreen;
